
import {buildProjection, itemProgressFromItems, itemKey} from './boxChampProjection';
import {buildZones} from './boxChampZones';

const boxesFrom = placement =>
  (placement && (Array.isArray(placement.boxes) ? placement.boxes : placement.box)) || [];

const hasChampPlan = placement =>
  boxesFrom(placement).some(b => b && b.source === 'box_champ');

export const champPlacementFrom = (...candidates) =>
  candidates.find(hasChampPlan) || null;

const firstImage = entry => {
  const images = Array.isArray(entry.images) ? entry.images : [];
  return (images[0] && images[0].image) || null;
};

const collectLabels = (items, labels, byProduct) => {
  const remember = (entry, key) => {
    const label = {name: entry.name, sku: entry.sku, image: firstImage(entry)};
    if (key != null && !labels[key]) labels[key] = label;
    if (entry.product_id != null && !byProduct[entry.product_id]) {
      byProduct[entry.product_id] = label;
    }
  };

  (Array.isArray(items) ? items : []).forEach(item => {
    if (!item || item.order_item_id == null) return;
    remember(item, itemKey(item.order_item_id, null));
    (Array.isArray(item.child_items) ? item.child_items : []).forEach(child => {
      if (!child) return;
      remember(
        child,
        child.kit_product_id == null
          ? null
          : itemKey(item.order_item_id, child.kit_product_id)
      );
    });
  });
};

const keysOfTreeItem = item => {
  const children = Array.isArray(item.child_items) ? item.child_items : [];
  const expanded =
    (item.product_type === 'individual' || item.product_type === 'depends') &&
    children.some(c => c && c.kit_product_id != null);
  if (expanded) {
    return children
      .filter(c => c && c.kit_product_id != null)
      .map(c => itemKey(item.order_item_id, c.kit_product_id));
  }
  return [itemKey(item.order_item_id, null)];
};

export function buildGuidedView({orderTree, placement} = {}) {
  if (!hasChampPlan(placement)) return null;

  const unscanned = (orderTree && orderTree.unscanned_items) || [];
  const scanned = (orderTree && orderTree.scanned_items) || [];
  const notFulfilled = (orderTree && orderTree.not_fulfilled_items) || [];
  const allItems = [...unscanned, ...scanned];

  const itemProgress = itemProgressFromItems(allItems);
  notFulfilled.forEach(item => {
    if (!item || item.order_item_id == null) return;
    const scannedQty = Number(item.scanned_qty) || 0;
    itemProgress[itemKey(item.order_item_id, item.kit_product_id == null ? null : item.kit_product_id)] = {scanned: scannedQty, qty: scannedQty};
    (Array.isArray(item.child_items) ? item.child_items : []).forEach(child => {
      if (!child || child.kit_product_id == null) return;
      const childScanned = Number(child.scanned_qty) || 0;
      itemProgress[itemKey(item.order_item_id, child.kit_product_id)] = {
        scanned: childScanned,
        qty: childScanned
      };
    });
  });

  const projection = buildProjection({
    boxes: boxesFrom(placement),
    orderItemBoxes: placement.order_item_boxes,
    itemProgress,
  });
  const zones = buildZones(projection);

  const labels = {};
  const labelsByProduct = {};
  collectLabels([...allItems, ...notFulfilled], labels, labelsByProduct);

  const activeItemIds = [];
  if (zones.active) {
    const seen = {};
    zones.active.rows.forEach(r => {
      if (r.filled >= r.target) return;
      const id = r.row.order_item_id;
      if (id == null || seen[id]) return;
      seen[id] = true;
      activeItemIds.push(id);
    });
  }

  const activeItems = activeItemIds
    .map(id => unscanned.find(item => item && item.order_item_id === id))
    .filter(Boolean);

  const overflowSet = {};
  projection.overflowKeys.forEach(key => { overflowSet[key] = true; });
  const unboxedItems = unscanned.filter(item => {
    if (!item || item.order_item_id == null) return false;
    const keys = keysOfTreeItem(item);
    return keys.length > 0 && keys.every(key => overflowSet[key]);
  });

  return {projection, zones, labels, labelsByProduct, activeItemIds, activeItems, unboxedItems};
}

const NO_NOT_FULFILLED = [];
export const createLiveTreeMemo = () => {
  let src = null;
  let nf = null;
  let out = null;
  return (order, notFulfilled) => {
    if (!order) return order;
    const list = notFulfilled || NO_NOT_FULFILLED;
    if (src !== order || nf !== list) {
      src = order;
      nf = list;
      out = {...order, not_fulfilled_items: list};
    }
    return out;
  };
};

export const createGuidedViewMemo = () => {
  let cache = null;
  return (orderTree, placementCandidates) => {
    if (!orderTree) return null;
    const placement = champPlacementFrom(...placementCandidates);
    if (!placement) return null;
    const boxes = boxesFrom(placement);
    if (
      cache &&
      cache.boxes === boxes &&
      cache.oib === placement.order_item_boxes &&
      cache.unscanned === orderTree.unscanned_items &&
      cache.scanned === orderTree.scanned_items &&
      cache.notFulfilled === orderTree.not_fulfilled_items
    ) {
      return cache.view;
    }
    const view = buildGuidedView({orderTree, placement});
    cache = {
      boxes,
      oib: placement.order_item_boxes,
      unscanned: orderTree.unscanned_items,
      scanned: orderTree.scanned_items,
      notFulfilled: orderTree.not_fulfilled_items,
      view,
    };
    return view;
  };
};
